import React, { useEffect, useRef, useCallback, useState, useMemo } from "react";
import useExploreFilter from "./hooks/useExploreFilter";
import useExploreData from "./hooks/useExploreData";
import useExploreMap from "./hooks/useExploreMap";
import { ExplorerMarker, ExplorerMarkerMobile } from "./utils/markers";
import { estateExploreService } from "../../services/estateExplore/estateExploreService";
import legacy, { clear_map, clear_land_polygons, clear_arch_polygons } from "../../utils/legacy";
import DesktopLayout from "./layouts/DesktopLayout";
import MobileLayout from "./layouts/MobileLayout";
import { v4 as uuidv4 } from "uuid";

/**
 * 매물탐색 통합 컴포넌트
 *
 * @param {string}  props.view   — "desktop" | "mobile" | "tablet" | 미지정(자동)
 * @param {Object}  props.state  — { history: "show" | "hide" | "destroy" }
 */
const EstateExplore = ({ view: viewProp, state = {} }) => {
  const resolvedView =
    viewProp || (window.innerWidth >= 1024 ? "desktop" : "mobile");
  const isDesktop = resolvedView === "desktop";

  const sideviewRef = useRef(null);
  const isHiddenRef = useRef(false);

  // 맵 조작 시 바텀시트 닫기용 콜백 ref
  const mapInteractionRef = useRef(null);
  const handleMapInteraction = useCallback(() => {
    mapInteractionRef.current?.();
  }, []);

  // 마커 클릭 시 바텀시트 열기용 콜백 ref
  const markerSelectRef = useRef(null);
  const handleMarkerSelect = useCallback(() => {
    markerSelectRef.current?.();
  }, []);

  // ── Hooks ──

  const filter = useExploreFilter();
  const { payload, payloadRef } = filter;

  const dataHook = useExploreData({
    payloadRef,
    sideviewRef,
    view: resolvedView,
  });

  // 단일 마커 클릭 시 바로 상세페이지 열기
  const drawPolygonForEstateRef = useRef(null);
  const handleOpenDetailFromMap = useCallback(
    (suid, markerData) => {
      if (!suid || !sideviewRef.current) return;
      legacy.open_sale_to_sideview(sideviewRef.current, suid, {
        mobileFullScreen: !isDesktop,
        handler: {
          show: () => {
            if (markerData && drawPolygonForEstateRef.current) {
              drawPolygonForEstateRef.current(markerData);
            }
          },
          close: () => {
            clear_land_polygons();
          },
        },
      });
    },
    [sideviewRef, isDesktop]
  );

  const mapHook = useExploreMap({
    payloadRef,
    sideviewRef,
    loadData: dataHook.loadData,
    fetchList: dataHook.fetchList,
    MarkerClass: isDesktop ? ExplorerMarker : ExplorerMarkerMobile,
    onMapInteraction: handleMapInteraction,
    onMarkerSelect: handleMarkerSelect,
    onOpenDetail: handleOpenDetailFromMap,
  });

  // drawPolygonForEstate를 ref로 보관 (mapHook 생성 후)
  drawPolygonForEstateRef.current = mapHook.drawPolygonForEstate;

  // ── 라이프사이클: state.history ──

  useEffect(() => {
    if (state.history === "show") {
      let wasHidden = false;
      if (isHiddenRef.current) {
        wasHidden = true;
        dataHook.loadData(payloadRef.current, 0);
        isHiddenRef.current = false;
      }
      mapHook.handleMap(true, wasHidden);
    } else if (state.history === "hide") {
      isHiddenRef.current = true;
      mapHook.handleMap(false);
    }
  }, [state]);

  // ── 마운트/언마운트 ──

  useEffect(() => {
    mapHook.handleMap(true, true);

    const cleanupIdle = mapHook.setupMapIdleListener(() => {
      getCurrentAddress();
    });

    dataHook.loadData(payloadRef.current, 0);

    return () => {
      mapHook.handleMap(false);
      cleanupIdle();
    };
  }, []);

  // ── 필터 변경 시 리로드 ──

  const firstLoadRef = useRef(true);
  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      return;
    }

    dataHook.setOffset(0);
    dataHook.loadData(payload, 0);

    if (legacy.timer_redraw != null) {
      clearTimeout(legacy.timer_redraw);
      legacy.timer_redraw = null;
    }
    if (legacy.timer_area_redraw != null) {
      clearTimeout(legacy.timer_area_redraw);
      legacy.timer_area_redraw = null;
    }
    clear_map();
    clear_land_polygons?.();
    clear_arch_polygons?.();
    legacy.reload(false);
  }, [payload]);

  // ── 현재 주소 ──

  const getCurrentAddress = useCallback(async () => {
    try {
      const center = legacy.discoMap.getSltCenter();
      const lat = center.getLat();
      const lng = center.getLng();
      const pnuResult = await estateExploreService.location.getCenterPnu({ lat, lng });
      if (pnuResult?.pnu) {
        const lv = legacy.map.getLevel();
        await estateExploreService.region.getByPnu({
          pnu: pnuResult.pnu,
          lv,
        });
      }
    } catch (err) {
      // 주소 조회 실패는 무시
    }
  }, []);

  // ── 검색 ──

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRootId = useMemo(() => `estate-explore-search-${uuidv4()}`, []);
  const searchCallbacksRef = useRef({});

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleCloseSearch = useCallback(() => {
    const jq = legacy.jQuery || legacy.$;
    const rootEl = document.getElementById(searchRootId);
    if (jq && rootEl) {
      jq(rootEl).find("input").blur();
    }
    setIsSearchOpen(false);
  }, [searchRootId]);

  const moveToSearchResult = useCallback(
    (updateInfo) => {
      if (updateInfo?.x == null || updateInfo?.y == null) return;

      const fullAddr =
        updateInfo.address === "undefined" ? "" : updateInfo.address || "";

      legacy.s_his?.splice?.({
        type: updateInfo.selType,
        address: updateInfo.selValue,
        subValue: updateInfo.selSubValue || "",
        lat: updateInfo.y,
        lng: updateInfo.x,
        fullAddr,
      });

      setIsSearchOpen(false);

      const targetLatLng = new legacy.daum.maps.LatLng(
        Number(updateInfo.y),
        Number(updateInfo.x)
      );

      const afterIdle = async () => {
        await getCurrentAddress();
        dataHook.loadData(payloadRef.current, 0);
      };

      const waitForIdle = () => {
        const onIdle = () => {
          legacy.map.removeListener("idle", onIdle);
          afterIdle();
        };
        legacy.map.addListener("idle", onIdle);
      };

      if (typeof legacy.moveOnMap === "function") {
        legacy.moveOnMap(3, targetLatLng, true, waitForIdle);
      } else {
        legacy.map.setLevel(3);
        legacy.discoMap.panTo(targetLatLng);
        waitForIdle();
      }
    },
    [getCurrentAddress, dataHook, payloadRef]
  );

  const handleSearchSelect = useCallback(
    (selection) => {
      if (typeof legacy.onSelectSearchResultFromPop !== "function") return;

      legacy.onSelectSearchResultFromPop(
        selection,
        (lat, lng, address, road_address) => {
          moveToSearchResult({
            selValue: selection.value,
            selType: selection.type,
            selSubValue: selection.subValue || "",
            x: lng,
            y: lat,
            address: address || "",
            road_address: road_address || "",
          });
        },
        () => {} // 실패 시 무시
      );
    },
    [moveToSearchResult]
  );

  const handleSearchHistorySelect = useCallback(
    (searchHistoryElement) => {
      const jq = legacy.jQuery || legacy.$;
      if (!jq) return;

      const item = jq(searchHistoryElement);
      moveToSearchResult({
        selValue: item.attr("data-value") || "",
        selType: item.attr("data-type") || "",
        selSubValue: item.attr("data-sub-value") || "",
        x: item.attr("data-lng"),
        y: item.attr("data-lat"),
        address: item.attr("data-full-addr") || "",
        road_address: "",
      });
    },
    [moveToSearchResult]
  );

  // 콜백을 ref로 보관 (useEffect 클로저 안정성)
  searchCallbacksRef.current = {
    onClose: handleCloseSearch,
    onSelect: handleSearchSelect,
    onHistorySelect: handleSearchHistorySelect,
  };

  // 검색 UI 마운트/언마운트
  useEffect(() => {
    if (!isSearchOpen) return;
    if (typeof legacy.DiscoSearchUI2 !== "function") return;

    const searchUi = new legacy.DiscoSearchUI2({
      id: searchRootId,
      onSelectDone: (selection) =>
        searchCallbacksRef.current.onSelect(selection),
      hasBackBtn: true,
      onClickBackBtn: () => searchCallbacksRef.current.onClose(),
      hasBldgBtn: false,
    });

    const defaultContainer = searchUi.getDefaultContainer();
    const jq = legacy.jQuery || legacy.$;

    if (typeof legacy.render_search_history === "function") {
      legacy.render_search_history(defaultContainer);
    }

    defaultContainer.off(".exploreSearch");
    defaultContainer.on(
      "click.exploreSearch",
      ".clear-search-history",
      function () {
        legacy.s_his?.clear?.();
        legacy.render_search_history?.(defaultContainer);
      }
    );
    defaultContainer.on(
      "click.exploreSearch",
      ".search-history",
      function () {
        searchCallbacksRef.current.onHistorySelect(this);
      }
    );
    defaultContainer.on(
      "click.exploreSearch",
      ".del-search-history",
      function (event) {
        jq(this)
          .parent(".search-history")
          .each((_, element) => {
            legacy.s_his?.del?.(jq(element).attr("data-value"));
          });
        legacy.render_search_history?.(defaultContainer);
        event.stopPropagation();
      }
    );

    searchUi.setViewHeight(window.innerHeight);
    searchUi.resetSearchUI();
    searchUi.focusOnQueryInput();

    return () => {
      defaultContainer.off(".exploreSearch");
      const rootEl = document.getElementById(searchRootId);
      if (rootEl) rootEl.innerHTML = "";
    };
  }, [isSearchOpen, searchRootId]);

  // ── 렌더링 ──

  // PC: 왼쪽 리스트 패널 영역에만 표시 (position: absolute, listPanel 기준)
  // 모바일: 전체 화면
  const searchOverlayDesktop = isSearchOpen ? (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1065,
        backgroundColor: "#fff",
        pointerEvents: "all",
      }}
    >
      <div id={searchRootId} style={{ width: "100%", height: "100%" }} />
    </div>
  ) : null;

  const searchOverlayMobile = isSearchOpen ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1065,
        backgroundColor: "#fff",
        pointerEvents: "all",
      }}
    >
      <div id={searchRootId} style={{ width: "100%", height: "100%" }} />
    </div>
  ) : null;

  // hide 상태에서는 DOM을 유지하되 보이지 않게 (레거시 클릭 차단 방지)
  const isHidden = state.history === "hide";

  if (isDesktop) {
    return (
      <DesktopLayout
        filter={filter}
        dataHook={dataHook}
        mapHook={mapHook}
        sideviewRef={sideviewRef}
        mapInteractionRef={mapInteractionRef}
        markerSelectRef={markerSelectRef}
        onSearch={handleOpenSearch}
        searchOverlay={searchOverlayDesktop}
        hidden={isHidden}
      />
    );
  }

  return (
    <MobileLayout
      filter={filter}
      dataHook={dataHook}
      mapHook={mapHook}
      sideviewRef={sideviewRef}
      mapInteractionRef={mapInteractionRef}
      markerSelectRef={markerSelectRef}
      onSearch={handleOpenSearch}
      searchOverlay={searchOverlayMobile}
      hidden={isHidden}
    />
  );
};

export default EstateExplore;
